import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import TextField from 'studio/components/TextField';
import Select from 'studio/components/Select';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import DownIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import UpIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import AddCircleIcon from '@mui/icons-material/AddCircleRounded';
import { TenantsIcon } from 'components/Icons';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccountRounded';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmailRounded';
import TranslateIcon from '@mui/icons-material/TranslateRounded';
import SecurityIcon from '@mui/icons-material/SecurityRounded';
import FaceIcon from '@mui/icons-material/FaceRounded';
import Tabs from 'studio/components/Tabs/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from 'studio/components/Checkbox';
import { veezooUrls } from 'config/constants';
import { Controller, useFieldArray } from 'react-hook-form/dist/index.ie11';
import HelpButton from 'studio/components/HelpButton';
import _ from 'lodash';
import * as yup from 'yup';

const userAttributes = {
  tenant: {
    title: 'Tenant',
    icon: <TenantsIcon />,
    isHidden: user => !user.organizationUsesTenants
  },
  type: {
    title: 'Type',
    icon: <SupervisorAccountIcon />
  },
  firstName: {
    title: 'First name',
    icon: <FaceIcon />
  },
  lastName: {
    title: 'Last name',
    icon: <FaceIcon />
  },
  email: {
    title: 'E-mail',
    icon: <AlternateEmailIcon />
  },
  language: {
    title: 'Language',
    icon: <TranslateIcon />
  },
  roles: {
    title: 'Roles',
    icon: <SecurityIcon />,
    isMulti: true
  }
};

export const getSchema = user =>
  yup.object().shape(
    Object.assign(
      // for each user attribute...
      ...Object.entries(userAttributes).map(([key, userAttribute]) => ({
        // ...define the validation for its mappings
        [key]: yup.array().of(
          yup.object().shape({
            value: userAttribute.isHidden?.(user) ? yup.string().optional() : yup.string().required(),
            // check that the condition is either null (disabled) or not empty
            condition: yup
              .string()
              .nullable()
              .test('condition', value => value === null || value !== '')
          })
        )
      }))
    )
  );

const useStyles = makeStyles(theme => {
  return {
    root: {},
    tabContent: {
      borderBottom: '1px solid',
      borderBottomColor: theme.palette.divider,
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3)
    },
    mapping: {
      '& .MuiFormControl-root': {
        marginTop: 0,
        marginBottom: 0
      },
      marginBottom: theme.spacing(1),
      '&:first-child': {
        marginTop: theme.spacing(3)
      },
      '&:last-child': {
        marginBottom: 0
      }
    },
    valueInput: {
      width: 160,
      marginRight: theme.spacing(2)
    },
    conditionInput: {
      flex: '3.5 1 0',
      marginRight: theme.spacing(2)
    },
    descriptionInput: {
      flex: '2 1 0'
    },
    removeButton: {
      width: 44,
      height: 44
    },
    moveButton: {
      padding: 0,
      width: 22,
      height: 22
    }
  };
});

const UserAttributeMappings = ({ fieldName = 'userAttributeMappings', user, userTypes, languages, roles, form }) => {
  const classes = useStyles();

  // the index of the selected user attribute tab
  const [userAttributeTabIndex, setUserAttributeTabIndex] = useState(0);

  const visibleUserAttributes = _.pickBy(userAttributes, userAttribute => !userAttribute.isHidden?.(user));

  const valueComponentOverrides = {
    type: {
      as: Select,
      // wrap the user type in single quotes to make it a string literal (in the user attribute mapping syntax)
      values: Object.values(userTypes).map(userType => `'${userType}'`),
      labels: Object.values(userTypes)
    },
    language: {
      as: Select,
      values: languages.map(language => `'${language.data.id}'`),
      labels: languages.map(language => language.data.name)
    },
    roles: {
      as: Select,
      values: roles?.map(role => `'${role.id}'`),
      labels: roles?.map(role => role.name)
    }
  };

  // returns the full name of a user attribute mapping field
  const mappingName = (...keys) => [fieldName, ...keys].join('.');

  // returns the relative URL for the documentation given the specified hash
  const docsUrl = hash => `/single-sign-on/user-attributes${hash ? `#${hash}` : ''}`;

  // object containing a field array for each user attribute
  const fieldArrays = Object.assign(
    ...Object.keys(userAttributes).map(key => ({
      [key]: useFieldArray({
        control: form.control,
        name: mappingName(key)
      })
    }))
  );

  // the form data
  const data = form.watch();

  // enables / disables the condition with the specified user attribute key and field index
  const enableCondition = (key, index, enable = true) => {
    // set condition to empty string to enable it and to null to disable it
    const value = enable ? '' : null;
    form.setValue(mappingName(key, index, 'condition'), value, { shouldDirty: true, shouldValidate: !enable });
  };

  // checks whether a user attribute mapping has an error
  const hasError = (...keys) => !!keys.reduce((obj, key) => obj?.[key], form.errors[fieldName]);

  // specifies whether any field inside the currently active tab has an error
  const currentTabHasError = hasError(Object.keys(visibleUserAttributes)[userAttributeTabIndex]);

  // sets the user attribute mapping tab to the one with an error, if any, after the form has been submitted
  useEffect(() => {
    const index = Object.keys(visibleUserAttributes).findIndex(key => hasError(key));
    if (index >= 0) {
      setUserAttributeTabIndex(index);
    }
  }, [form.formState.submitCount]);

  return (
    <>
      <Tabs
        onChange={(_, index) => setUserAttributeTabIndex(index)}
        value={userAttributeTabIndex}
        className={currentTabHasError ? 'error' : ''}
      >
        {Object.entries(visibleUserAttributes).map(([key, userAttribute]) => (
          <Tab
            key={key}
            label={
              <div>
                {userAttribute.icon}
                {userAttribute.title}
              </div>
            }
            className={hasError(key) ? 'error' : ''}
          />
        ))}
      </Tabs>

      {Object.entries(visibleUserAttributes).map(([key, userAttribute], tabIndex) => {
        const { fields, move, remove, append } = fieldArrays[key];
        return (
          <div key={key} id={key} hidden={tabIndex !== userAttributeTabIndex} className={classes.tabContent}>
            Define the mapping(s) that determine the user&apos;s <b>{userAttribute.title.toLowerCase()}</b>.{' '}
            {userAttribute.isMulti ? (
              <>
                <b>All mappings</b> whose conditions match are applied.
              </>
            ) : (
              <>
                The <b>first mapping</b> whose condition matches is applied.
              </>
            )}
            <ul>
              <li>
                <b>Value</b>: the value to assign to the user attribute, if the condition holds
                <HelpButton docsUrl={docsUrl('value')} small />
              </li>
              <li>
                <b>Condition</b>: The condition which determines if the mapping should be used (optional)
                <HelpButton docsUrl={docsUrl('condition')} small />
              </li>
              <li>
                <b>Description</b>: the description for the mapping (optional)
                <HelpButton docsUrl={docsUrl('description')} small />
              </li>
            </ul>
            Read the{' '}
            <Link href={`${veezooUrls.docs}${docsUrl()}`} target="_blank" rel="noreferrer">
              documentation
            </Link>{' '}
            for more information about user attribute mappings.
            <div>
              {fields.map((field, fieldIndex) => {
                const conditionValue = data[fieldName]?.[key]?.[fieldIndex]?.condition;
                const conditionIsDisabled = typeof conditionValue !== 'string';
                return (
                  <Box key={field.id} display="flex" className={classes.mapping}>
                    <div className={classes.valueInput}>
                      <Controller
                        control={form.control}
                        as={TextField}
                        name={mappingName(key, fieldIndex, 'value')}
                        defaultValue={field.value || null}
                        label="Value"
                        error={hasError(key, fieldIndex, 'value')}
                        {...valueComponentOverrides[key]}
                      />
                    </div>
                    <div>
                      <Checkbox
                        label="if"
                        value={!conditionIsDisabled}
                        // set condition to empty string to enable it and to null to disable it
                        onChange={checked => enableCondition(key, fieldIndex, checked)}
                      />
                    </div>
                    <div className={classes.conditionInput}>
                      <Controller
                        control={form.control}
                        name={mappingName(key, fieldIndex, 'condition')}
                        defaultValue={field.condition || null}
                        render={({ ref, onChange, onBlur, value }) => (
                          <TextField
                            ref={ref}
                            onChange={onChange}
                            value={value}
                            label="Condition"
                            disabled={conditionIsDisabled}
                            error={!conditionIsDisabled && hasError(key, fieldIndex, 'condition')}
                            onClick={_ => {
                              if (conditionIsDisabled) {
                                enableCondition(key, fieldIndex);
                                setTimeout(() => ref.current.focus(), 50);
                              }
                            }}
                            onBlur={event => {
                              if (conditionValue === '') {
                                enableCondition(key, fieldIndex, false);
                              }
                              onBlur(event);
                            }}
                          />
                        )}
                      />
                    </div>
                    <div className={classes.descriptionInput}>
                      <Controller
                        control={form.control}
                        as={TextField}
                        name={mappingName(key, fieldIndex, 'description')}
                        defaultValue={field.description || null}
                        label="Description"
                      />
                    </div>
                    <div>
                      <Tooltip title="Remove mapping" placement="top">
                        <IconButton size="large" className={classes.removeButton} onClick={_ => remove(fieldIndex)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                    <div>
                      <Tooltip title="Move mapping up" placement="top">
                        <span>
                          <IconButton
                            size="large"
                            className={classes.moveButton}
                            onClick={_ => move(fieldIndex, fieldIndex - 1)}
                            disabled={fieldIndex <= 0}
                          >
                            <UpIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <br />
                      <Tooltip title="Move mapping down" placement="top">
                        <span>
                          <IconButton
                            size="large"
                            className={classes.moveButton}
                            onClick={_ => move(fieldIndex, fieldIndex + 1)}
                            disabled={fieldIndex >= fields.length - 1}
                          >
                            <DownIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </div>
                  </Box>
                );
              })}
            </div>
            <Tooltip title="Add mapping" placement="top">
              <IconButton size="large" onClick={_ => append({})}>
                <AddCircleIcon />
              </IconButton>
            </Tooltip>
          </div>
        );
      })}
    </>
  );
};

export default UserAttributeMappings;
